import { Button, Dialog } from "@app/ui";
import { useState } from "react";

import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { formatDuration } from "../../lib/format.js";
import type { DayPlan, PlanSlot } from "./plan-day.js";

interface Props {
  /** Local midnight of the day being planned. */
  day: Date;
  plan: DayPlan;
  /** Apply the CHECKED proposals (reschedule each to its slot). */
  onApply: (slots: PlanSlot[]) => void;
  onClose: () => void;
}

/**
 * "Plan my day" — shows the proposed time slots from {@link planDay} for the
 * focused strip day. Every proposal starts checked; unticking drops it from
 * the apply. Items the free time couldn't hold are listed read-only.
 */
export function PlanDayDialog({ day, plan, onApply, onClose }: Props) {
  const { t } = useI18n();
  const tf = useTimeFormat();
  const locale = useAppLocale();
  const [checked, setChecked] = useState<Set<string>>(() => new Set(plan.slots.map((s) => s.task.id)));

  const dayLabel = day.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });
  const picked = plan.slots.filter((s) => checked.has(s.task.id));

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Dialog open onClose={onClose} title={t("priorities.plan.title", { day: dayLabel })} className="max-w-md">
      <div className="flex flex-col gap-4">
        {plan.slots.length === 0 && plan.unfit.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("priorities.plan.empty")}</p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{t("priorities.plan.intro")}</p>
            {plan.slots.length > 0 && (
              <ul className="flex flex-col gap-1">
                {plan.slots.map((s) => {
                  const end = new Date(s.start.getTime() + s.minutes * 60_000);
                  return (
                    <li key={s.task.id}>
                      <label className="flex cursor-pointer items-center gap-2.5 rounded-md px-1 py-1 text-sm hover:bg-accent/30">
                        <input
                          type="checkbox"
                          checked={checked.has(s.task.id)}
                          onChange={() => toggle(s.task.id)}
                          className="h-4 w-4 shrink-0 rounded border-input"
                        />
                        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                          {tf.hmDate(s.start)}–{tf.hmDate(end)}
                        </span>
                        <span className="min-w-0 flex-1 truncate">
                          {s.task.title || <em className="text-muted-foreground">{t("agenda.untitled")}</em>}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground">{formatDuration(s.minutes)}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            {plan.unfit.length > 0 && (
              <div className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
                <p className="mb-1 font-medium">{t("priorities.plan.unfit")}</p>
                <ul className="flex flex-col gap-0.5">
                  {plan.unfit.map(({ task, minutes }) => (
                    <li key={task.id} className="truncate">
                      {task.title || <em>{t("agenda.untitled")}</em>}
                      <span className="ml-1.5">({formatDuration(minutes)})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          {plan.slots.length > 0 && (
            <Button variant="primary" disabled={picked.length === 0} onClick={() => onApply(picked)}>
              {t("priorities.plan.apply", { count: picked.length })}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
